import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { ListChecks, Plus, Trash2, Loader2, CheckCircle2, XCircle, AlertTriangle, Bell, Sparkles, Mail, ShieldCheck, Send, Download } from "lucide-react";

const PRIO = { critical: "text-red-300 border-red-400/30 bg-red-400/10", important: "text-amber-300 border-amber-400/30 bg-amber-400/10", info: "text-slate-400 border-white/15" };
const TABS = ["Attention", "Tasks", "Approvals", "Notifications", "Settings"];

export default function OperationsHub() {
  const [tab, setTab] = useState("Attention");
  const [attention, setAttention] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", category: "operations", due_date: "", assignee_email: "", priority: "normal" });

  const load = async () => {
    const [a, t, ap, n] = await Promise.all([
      api.get("/political-os/attention").catch(() => ({ data: { items: [] } })),
      api.get("/political-os/tasks").catch(() => ({ data: { tasks: [] } })),
      api.get("/political-os/approvals").catch(() => ({ data: { approvals: [], my_roles: [] } })),
      api.get("/political-os/notifications").catch(() => ({ data: { notifications: [], unread: 0 } })),
    ]);
    setAttention(a.data); setTasks(t.data); setApprovals(ap.data); setNotifs(n.data);
  };
  useEffect(() => { load(); }, []);

  const addTask = async () => {
    if (!taskForm.title.trim()) return toast.error("Task title required");
    try { await api.post("/political-os/tasks", taskForm); toast.success("Task created"); setTaskForm({ ...taskForm, title: "" }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not create task")); }
  };
  const setTaskStatus = async (id, status) => { try { await api.put(`/political-os/tasks/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const delTask = async (id) => { try { await api.delete(`/political-os/tasks/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const decide = async (id, decision) => {
    try { await api.post(`/political-os/approvals/${id}/decide`, { decision }); toast.success(decision === "approve" ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(errMsg(e, "Could not decide")); }
  };
  const readAll = async () => { await api.post("/political-os/notifications/read-all"); load(); };

  if (!attention) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="operations-hub-page">
      <PageHeader eyebrow="Operations" title="Tasks, Approvals & Alerts"
        subtitle="Everything that needs a human decision — approval chains, task board and the daily attention feed." />
      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`ops-tab-${t.toLowerCase()}`}
            className={`text-xs px-4 py-2 rounded-full border transition-colors ${tab === t ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/15 text-slate-400 hover:text-white"}`}>
            {t}{t === "Notifications" && notifs?.unread ? ` (${notifs.unread})` : ""}{t === "Approvals" && approvals?.approvals?.filter(a => a.status === "pending").length ? ` (${approvals.approvals.filter(a => a.status === "pending").length})` : ""}
          </button>
        ))}
      </div>

      {tab === "Attention" && (
        <Card padded={false} data-testid="attention-card">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-300" /><h3 className="font-display text-lg font-bold">What needs my attention today?</h3>
          </div>
          <div className="divide-y divide-white/5">
            {attention.items.map((i, idx) => (
              <a key={idx} href={i.link} className="px-5 py-3.5 flex items-center gap-4 hover:bg-white/[0.03] transition-colors block" data-testid={`attention-item-${idx}`}>
                <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${PRIO[i.priority] || PRIO.info}`}>{i.priority}</span>
                <span className="min-w-0"><span className="block text-sm text-white">{i.title}</span><span className="block text-[11px] text-slate-500">{i.detail}</span></span>
              </a>
            ))}
          </div>
        </Card>
      )}

      {tab === "Tasks" && tasks && (
        <>
          <Card data-testid="add-task-card">
            <div className="flex flex-wrap gap-3 items-end">
              <Input label="Task" value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} placeholder="Approve CTV creative" className="flex-1 min-w-[200px]" data-testid="task-title-input" />
              <Select label="Category" value={taskForm.category} onChange={e => setTaskForm(f => ({ ...f, category: e.target.value }))}>
                {["advertising", "creative", "compliance", "fundraising", "event", "finance", "media", "research", "operations"].map(c => <option key={c}>{c}</option>)}
              </Select>
              <Input label="Due" type="date" value={taskForm.due_date} onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-due-input" />
              <Input label="Assignee email" value={taskForm.assignee_email} onChange={e => setTaskForm(f => ({ ...f, assignee_email: e.target.value }))} placeholder="optional" />
              <Btn onClick={addTask} data-testid="add-task-btn"><Plus className="w-4 h-4" /> Add</Btn>
            </div>
          </Card>
          <Card padded={false} data-testid="tasks-card">
            <div className="divide-y divide-white/5">
              {tasks.tasks.map(t => (
                <div key={t.id} className="px-5 py-3 flex items-center gap-3" data-testid={`task-${t.id}`}>
                  <button onClick={() => setTaskStatus(t.id, t.status === "done" ? "open" : "done")} data-testid={`task-toggle-${t.id}`}>
                    <CheckCircle2 className={`w-5 h-5 ${t.status === "done" ? "text-emerald-300" : "text-slate-600 hover:text-slate-400"}`} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm ${t.status === "done" ? "text-slate-500 line-through" : "text-white"}`}>{t.title}</div>
                    <div className="text-[11px] text-slate-500">{t.category}{t.due_date ? ` · due ${t.due_date}` : ""}{t.assignee_email ? ` · ${t.assignee_email}` : ""}</div>
                  </div>
                  {t.priority === "high" && <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />}
                  <button onClick={() => delTask(t.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {tasks.tasks.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">No tasks yet.</div>}
            </div>
          </Card>
        </>
      )}

      {tab === "Approvals" && approvals && (
        <Card padded={false} data-testid="approvals-card">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold">Approval requests</h3>
            <span className="text-[10px] text-slate-500">Your roles: {(approvals.my_roles || []).join(", ").replace(/_/g, " ") || "—"}</span>
          </div>
          <div className="divide-y divide-white/5">
            {approvals.approvals.map(a => (
              <div key={a.id} className="px-5 py-4" data-testid={`approval-${a.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${a.status === "approved" ? "text-emerald-300 border-emerald-400/30" : a.status === "rejected" ? "text-red-300 border-red-400/30" : "text-amber-300 border-amber-400/30"}`}>{a.status}</span>
                  <span className="text-sm text-white flex-1 min-w-0 truncate">{a.subject_name}</span>
                  {a.status === "pending" && a.can_decide && (
                    <div className="flex gap-2">
                      <button onClick={() => decide(a.id, "approve")} data-testid={`approve-btn-${a.id}`}
                        className="text-xs px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors">Approve</button>
                      <button onClick={() => decide(a.id, "reject")} data-testid={`reject-btn-${a.id}`}
                        className="text-xs px-3 py-1.5 rounded-full border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors">Reject</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  {a.chain.map((step, i) => (
                    <React.Fragment key={i}>
                      <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${i < a.current_step || a.status === "approved" ? "text-emerald-300 border-emerald-400/30" : i === a.current_step && a.status === "pending" ? "text-amber-300 border-amber-400/40 bg-amber-400/10" : "text-slate-600 border-white/10"}`}>{step.replace(/_/g, " ")}</span>
                      {i < a.chain.length - 1 && <span className="text-slate-700">→</span>}
                    </React.Fragment>
                  ))}
                </div>
                <div className="text-[10px] text-slate-600 mt-1.5">Requested by {a.requested_by} · {a.created_at?.slice(0, 10)}{a.history?.length ? ` · ${a.history.length} decision${a.history.length > 1 ? "s" : ""}` : ""}</div>
              </div>
            ))}
            {approvals.approvals.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">No approval requests. Request one from a campaign, or via API.</div>}
          </div>
        </Card>
      )}

      {tab === "Notifications" && notifs && (
        <Card padded={false} data-testid="notifications-card">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><Bell className="w-4 h-4 text-cyan-300" /> Notifications</h3>
            <button onClick={readAll} className="text-xs text-slate-500 hover:text-cyan-300" data-testid="read-all-btn">Mark all read</button>
          </div>
          <div className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
            {notifs.notifications.map(n => (
              <div key={n.id} className={`px-5 py-3 ${n.read ? "opacity-50" : ""}`} data-testid={`notification-${n.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIO[n.priority] || PRIO.info}`}>{n.category}</span>
                  <span className="text-sm text-white">{n.title}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{n.body} · {n.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
            ))}
            {notifs.notifications.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">Nothing yet.</div>}
          </div>
        </Card>
      )}

      {tab === "Settings" && <OpsSettingsPanel />}
    </div>
  );
}

// ------------------- Settings panel -------------------

function OpsSettingsPanel() {
  const [wb, setWb] = useState(null);
  const [wbRecipients, setWbRecipients] = useState("");
  const [bt, setBt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    try {
      const [w, b] = await Promise.all([
        api.get("/political-os/weekly-brief"),
        api.get("/political-os/budget-thresholds"),
      ]);
      setWb(w.data);
      setWbRecipients((w.data.recipients || []).join(", "));
      setBt(b.data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load settings"));
    }
  };
  useEffect(() => { load(); }, []);

  const saveWb = async (enabled) => {
    setSaving(true);
    try {
      const recipients = wbRecipients.split(/[,\s]+/).map(x => x.trim()).filter(Boolean);
      await api.put("/political-os/weekly-brief", { enabled, recipients });
      toast.success(enabled ? "Weekly brief enabled" : "Weekly brief disabled");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const testWb = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/political-os/weekly-brief/test");
      toast.success(`Weekly brief sent to ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e, "Could not send test brief")); } finally { setTesting(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data, headers } = await api.get("/political-os/weekly-brief/pdf", { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const cd = headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : "weekly-brief.pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Weekly brief PDF downloaded");
    } catch (e) { toast.error(errMsg(e, "Could not generate PDF")); } finally { setDownloading(false); }
  };

  const saveBt = async () => {
    setSaving(true);
    try {
      await api.put("/political-os/budget-thresholds", {
        manager_limit: bt.manager_limit === "" ? null : Number(bt.manager_limit),
        treasurer_limit: bt.treasurer_limit === "" ? null : Number(bt.treasurer_limit),
      });
      toast.success("Budget thresholds saved");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!wb || !bt) return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-300" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card data-testid="weekly-brief-card">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Weekly Executive Brief</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Every Monday morning we email leadership a recap of media spend, CTV reach, fundraising and public race changes.</p>
        <label className="flex items-center gap-3 py-2 cursor-pointer" data-testid="weekly-brief-toggle-label">
          <input type="checkbox" checked={wb.enabled} onChange={(e) => saveWb(e.target.checked)}
                 disabled={saving} data-testid="weekly-brief-toggle"
                 className="w-4 h-4 accent-cyan-400" />
          <span className="text-sm">Send this brief every Monday morning</span>
        </label>
        <div className="mt-3">
          <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Recipients (comma separated)</label>
          <input value={wbRecipients} onChange={(e) => setWbRecipients(e.target.value)}
                 data-testid="weekly-brief-recipients"
                 placeholder={wb.fallback_recipient || "candidate@example.com, chief-of-staff@example.com"}
                 className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
          <div className="text-[10px] text-slate-500 mt-1">If empty, defaults to the entity&apos;s official email ({wb.fallback_recipient || "not set"}).</div>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Btn onClick={() => saveWb(wb.enabled)} data-testid="weekly-brief-save-btn" disabled={saving}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save recipients
          </Btn>
          <Btn variant="secondary" onClick={testWb} data-testid="weekly-brief-test-btn" disabled={testing}>
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test to me
          </Btn>
          <Btn variant="secondary" onClick={downloadPdf} data-testid="weekly-brief-pdf-btn" disabled={downloading}>
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download PDF
          </Btn>
        </div>
      </Card>

      <Card data-testid="budget-thresholds-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Budget Control Center</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Set the dollar limits that decide who has to sign off on a campaign. Smaller spends stay with the campaign manager; bigger spends escalate to treasurer and candidate.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Manager approves alone up to (USD)</label>
            <input type="number" min="0" step="500"
                   value={bt.manager_limit ?? ""}
                   onChange={(e) => setBt({ ...bt, manager_limit: e.target.value })}
                   placeholder="25000"
                   data-testid="budget-manager-limit"
                   className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Manager + treasurer approves up to (USD)</label>
            <input type="number" min="0" step="1000"
                   value={bt.treasurer_limit ?? ""}
                   onChange={(e) => setBt({ ...bt, treasurer_limit: e.target.value })}
                   placeholder="100000"
                   data-testid="budget-treasurer-limit"
                   className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
          </div>
          <div className="text-[10px] text-slate-500">Above the treasurer limit runs the full approval chain including the candidate. Leave both blank to always require the full chain.</div>
          <Btn onClick={saveBt} disabled={saving} data-testid="budget-thresholds-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save thresholds
          </Btn>
        </div>
        <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-slate-500">
          {bt.note}
        </div>
      </Card>
    </div>
  );
}
